import { useState, useEffect } from "react";
import { useAuth } from "@/App";
import GlassCard from "@/components/GlassCard";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Search, Star, Clock, MapPin, ChevronRight, Check, Loader2, CalendarDays } from "lucide-react";
import { toast } from "sonner";

export default function CoachPage() {
  const { api } = useAuth();
  const [coaches, setCoaches] = useState([]);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [slots, setSlots] = useState([]);
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [booking, setBooking] = useState(false);
  const [bookings, setBookings] = useState([]);
  const [showBooking, setShowBooking] = useState(false);
  const [bookingStep, setBookingStep] = useState(1);
  const [bookingDetails, setBookingDetails] = useState({
    goal: "Teljesítmény",
    session_type: "online",
    focus_area: "",
    contact: "",
    notes: ""
  });

  useEffect(() => {
    Promise.all([
      api().get(`/coaches${search ? `?search=${search}` : ""}`),
      api().get("/bookings")
    ]).then(([cRes, bRes]) => {
      setCoaches(cRes.data);
      setBookings(bRes.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [api, search]);

  const loadSlots = async (coach) => {
    setSelectedCoach(coach);
    setShowBooking(true);
    setSelectedDate(null);
    setSelectedSlot(null);
    setBookingStep(1);
    setBookingDetails({
      goal: "Teljesítmény",
      session_type: "online",
      focus_area: "",
      contact: "",
      notes: ""
    });
    try {
      const res = await api().get(`/coaches/${coach.id}/slots`);
      setSlots(res.data);
    } catch {
      setSlots([]);
    }
  };

  const filteredSlots = selectedDate
    ? slots.filter(s => s.date === formatDate(selectedDate))
    : [];

  const handleBook = async () => {
    if (!selectedSlot || !selectedCoach) return;
    setBooking(true);
    try {
      const res = await api().post("/bookings", {
        coach_id: selectedCoach.id,
        slot_id: selectedSlot.id,
        notes: bookingDetails.notes,
        goal: bookingDetails.goal,
        session_type: bookingDetails.session_type,
        focus_area: bookingDetails.focus_area,
        contact: bookingDetails.contact
      });
      setBookings([...bookings, res.data]);
      setShowBooking(false);
      toast.success("Foglalás megerősítve!");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hiba a foglalásnál");
    } finally {
      setBooking(false);
    }
  };

  const updateBookingDetail = (field, value) => {
    setBookingDetails((prev) => ({ ...prev, [field]: value }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-8 h-8 border-2 border-[#00f260] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6 md:pt-20" data-testid="coach-page">
      <div className="flex items-center justify-between fade-in-up">
        <h1 className="font-heading text-3xl font-bold text-white tracking-tight">Coach</h1>
        <CalendarDays className="text-[#00f260]" size={24} />
      </div>

      {/* Search */}
      <div className="relative fade-in-up">
        <Search size={16} className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500" />
        <Input
          data-testid="coach-search-input"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Keresés szakértők között"
          className="bg-white/5 border-white/10 text-white pl-10 rounded-xl h-12 focus:border-[#00f260] focus:ring-[#00f260] placeholder:text-gray-500"
        />
      </div>

      {/* Coach Cards */}
      <div className="grid md:grid-cols-2 gap-4">
        {coaches.map((coach) => (
          <GlassCard key={coach.id} className="fade-in-up" testId={`coach-card-${coach.id}`}>
            <div className="flex items-start justify-between">
              <div>
                <p className="text-lg text-white font-semibold">{coach.name}</p>
                <p className="text-xs text-gray-400">{coach.specialty}</p>
              </div>
              <Badge className="bg-[#00f260]/10 text-[#00f260] border-0 text-xs">{coach.price?.toLocaleString()} Ft</Badge>
            </div>
            <div className="flex flex-wrap items-center gap-4 text-xs text-gray-400 mt-3">
              <span className="flex items-center gap-1"><Star size={12} className="text-[#00f260]" /> {coach.rating}</span>
              <span className="flex items-center gap-1"><Clock size={12} className="text-[#00f260]" /> {coach.sessions} alkalom</span>
              <span className="flex items-center gap-1"><MapPin size={12} className="text-[#00f260]" /> {coach.location}</span>
            </div>
            <p className="text-xs text-gray-500 mt-3">{coach.bio}</p>
            <button
              data-testid={`book-coach-${coach.id}`}
              onClick={() => loadSlots(coach)}
              className="mt-4 w-full neon-btn text-sm flex items-center justify-center gap-2"
            >
              Időpont foglalás <ChevronRight size={14} />
            </button>
          </GlassCard>
        ))}
      </div>

      {/* Bookings */}
      <GlassCard testId="bookings-card" className="fade-in-up">
        <div className="flex items-center justify-between mb-4">
          <p className="text-xs text-gray-400 uppercase tracking-wider">Foglalásaim</p>
          <Badge className="bg-white/5 border-0 text-xs text-gray-300">{bookings.length}</Badge>
        </div>
        {bookings.length > 0 ? (
          <div className="space-y-3">
            {bookings.map((b, i) => (
              <div key={b.id} className="flex items-center gap-3 p-3 bg-white/5 rounded-xl" data-testid={`booking-item-${i}`}>
                <div className="w-10 h-10 rounded-full bg-[#00f260]/10 flex items-center justify-center">
                  <Check size={16} className="text-[#00f260]" />
                </div>
                <div className="flex-1">
                  <p className="text-sm text-white font-medium">{b.coach_name}</p>
                  <p className="text-xs text-gray-400">{b.specialty} &middot; {b.date} {b.time}</p>
                  {(b.goal || b.session_type) && (
                    <p className="text-[10px] text-gray-500" data-testid={`booking-detail-${i}`}>
                      {b.goal} {b.session_type ? `• ${b.session_type}` : ""}
                    </p>
                  )}
                </div>
                <span className="text-xs text-[#00f260]">{b.price?.toLocaleString()} Ft</span>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6" data-testid="booking-empty">
            <p className="text-gray-500 text-sm">Még nincs foglalásod</p>
          </div>
        )}
      </GlassCard>

      <Dialog open={showBooking} onOpenChange={setShowBooking}>
        <DialogContent className="max-w-2xl bg-[#0a0a0a] border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="text-white">Időpont foglalás</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="flex items-center justify-between" data-testid="booking-step-indicator">
              <p className="text-xs text-gray-400 uppercase tracking-wider">Foglalás {bookingStep === 1 ? "1/2" : "2/2"}</p>
              <span className="text-[10px] text-gray-500">{bookingStep === 1 ? "Időpont" : "Részletek"}</span>
            </div>
            <p className="text-xs text-gray-400">{selectedCoach?.specialty} &middot; {selectedCoach?.price?.toLocaleString()} Ft / alkalom</p>

            {bookingStep === 1 && (
              <>
                <div className="flex justify-center" data-testid="booking-calendar">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={setSelectedDate}
                    className="rounded-xl border border-white/10"
                    disabled={(date) => date < new Date()}
                  />
                </div>

                {selectedDate && (
                  <div>
                    <p className="text-xs text-gray-400 mb-2">Elérhető idősávok - {formatDate(selectedDate)}</p>
                    {filteredSlots.length > 0 ? (
                      <div className="grid grid-cols-3 gap-2">
                        {filteredSlots.map(slot => (
                          <button
                            key={slot.id}
                            data-testid={`slot-${slot.time}`}
                            onClick={() => setSelectedSlot(slot)}
                            className={`p-2 rounded-lg text-sm text-center transition-all ${
                              selectedSlot?.id === slot.id
                                ? "bg-[#00f260] text-black font-medium"
                                : "bg-white/5 text-white hover:bg-white/10"
                            }`}
                          >
                            {slot.time}
                          </button>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-500 text-center py-4">Nincs elérhető időpont ezen a napon</p>
                    )}
                  </div>
                )}

                <button
                  data-testid="booking-next-btn"
                  onClick={() => setBookingStep(2)}
                  disabled={!selectedSlot}
                  className={`neon-btn w-full text-sm ${!selectedSlot ? "opacity-40 cursor-not-allowed" : ""}`}
                >
                  Tovább
                </button>
              </>
            )}

            {bookingStep === 2 && (
              <>
                <div className="p-3 rounded-xl bg-white/5 space-y-1" data-testid="booking-summary">
                  <p className="text-xs text-gray-400">Időpont</p>
                  <p className="text-sm text-white">{formatDate(selectedDate)} {selectedSlot?.time}</p>
                  <p className="text-xs text-gray-500">{selectedCoach?.name} • {selectedCoach?.price?.toLocaleString()} Ft</p>
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-gray-400">Cél</label>
                  <select
                    data-testid="booking-goal-select"
                    value={bookingDetails.goal}
                    onChange={(e) => updateBookingDetail('goal', e.target.value)}
                    className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm"
                  >
                    <option value="Teljesítmény">Teljesítmény</option>
                    <option value="Testsúly">Testsúly</option>
                    <option value="Regeneráció">Regeneráció</option>
                    <option value="Verseny">Versenyre készülés</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-gray-400">Session típus</label>
                  <select
                    data-testid="booking-session-type"
                    value={bookingDetails.session_type}
                    onChange={(e) => updateBookingDetail('session_type', e.target.value)}
                    className="w-full bg-black/20 border border-white/10 text-white rounded-xl h-11 px-3 text-sm"
                  >
                    <option value="online">Online</option>
                    <option value="személyes">Személyes</option>
                  </select>
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-gray-400">Fókusz terület</label>
                  <Input
                    data-testid="booking-focus-input"
                    value={bookingDetails.focus_area}
                    onChange={(e) => updateBookingDetail('focus_area', e.target.value)}
                    placeholder="Pl. futótechnika, erőnlét"
                    className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-gray-400">Elérhetőség</label>
                  <Input
                    data-testid="booking-contact-input"
                    value={bookingDetails.contact}
                    onChange={(e) => updateBookingDetail('contact', e.target.value)}
                    placeholder="Email / Zoom / Telefon"
                    className="bg-black/20 border-white/10 text-white rounded-xl h-11 focus:border-[#00f260] focus:ring-[#00f260]"
                  />
                </div>

                <div className="space-y-2">
                  <label className="text-xs text-gray-400">Megjegyzés</label>
                  <textarea
                    data-testid="booking-notes-input"
                    value={bookingDetails.notes}
                    onChange={(e) => updateBookingDetail('notes', e.target.value)}
                    placeholder="Rövid cél, sérülés, preferencia..."
                    className="w-full min-h-[90px] bg-black/20 border border-white/10 text-white rounded-xl p-3 text-sm focus:border-[#00f260] focus:ring-[#00f260]"
                  />
                </div>

                <div className="flex flex-col md:flex-row gap-3">
                  <button
                    data-testid="booking-back-btn"
                    onClick={() => setBookingStep(1)}
                    className="w-full md:w-auto px-6 py-3 rounded-full border border-white/10 text-gray-300 hover:text-white hover:border-white/30 transition-all text-sm"
                  >
                    Vissza
                  </button>
                  <button
                    data-testid="confirm-booking-btn"
                    onClick={handleBook}
                    disabled={booking}
                    className="neon-btn w-full text-sm"
                  >
                    {booking ? <Loader2 size={16} className="animate-spin mx-auto" /> : "Foglalás véglegesítése"}
                  </button>
                </div>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function formatDate(date) {
  if (!date) return "";
  const d = new Date(date);
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
